import React, { useEffect, useState } from "react";
import { Plus, Minus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { PageTitle, Field, TextInput } from "./shared";

const CATEGORIES = [
  { value: "all", label: "ALL" },
  { value: "tuber", label: "TUBERS" },
  { value: "seedling", label: "SEEDLINGS" },
  { value: "merchandise", label: "MERCHANDISE" },
  { value: "tag_stock", label: "TAG STOCK" },
];

const Inventory = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [newItem, setNewItem] = useState({ name: "", category: "tuber", stock: 0, low_stock_threshold: 5 });

  useEffect(() => {
    api.get("/admin/inventory").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const adjust = async (item, delta) => {
    const { data } = await api.post(`/admin/inventory/${item.id}/adjust`, { delta });
    setItems((l) => l.map((x) => (x.id === item.id ? { ...x, stock: data.stock } : x)));
  };

  const add = async (e) => {
    e.preventDefault();
    if (!newItem.name.trim()) { toast.error("Give the item a name"); return; }
    const { data } = await api.post("/admin/inventory", { ...newItem, stock: Number(newItem.stock) || 0, low_stock_threshold: Number(newItem.low_stock_threshold) || 0 });
    setItems((l) => [...(l || []), data].sort((a, b) => a.name.localeCompare(b.name)));
    setNewItem({ name: "", category: newItem.category, stock: 0, low_stock_threshold: 5 });
    toast.success(`${data.name} added to inventory`);
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.name} from inventory?`)) return;
    await api.delete(`/admin/inventory/${item.id}`);
    setItems((l) => l.filter((x) => x.id !== item.id));
    toast.success("Item removed");
  };

  const filtered = (items || []).filter((i) => filter === "all" || i.category === filter || (filter === "seedling" && i.category === "seed"));
  const lowCount = (items || []).filter((i) => i.stock <= i.low_stock_threshold).length;

  return (
    <div data-testid="console-inventory">
      <PageTitle kicker="SHARED ADMIN · STOCK" title="INVENTORY">
        {lowCount > 0 && (
          <span className="inline-flex items-center gap-2 rounded-full bg-orange/15 text-orange px-4 py-2 text-xs uppercase tracking-[0.14em] font-bold" data-testid="inventory-low-count">
            <AlertTriangle className="w-3.5 h-3.5" /> {lowCount} LOW
          </span>
        )}
      </PageTitle>

      <form onSubmit={add} className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-5 mb-6 flex flex-wrap items-end gap-3" data-testid="new-inventory-form">
        <Field label="ITEM NAME"><TextInput value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} data-testid="new-inventory-name" className="w-56" /></Field>
        <Field label="CATEGORY">
          <select value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })} data-testid="new-inventory-category" className="rounded-full px-4 py-2.5 bg-cream border border-plum-deep/15 text-sm focus:outline-none">
            <option value="tuber">TUBER</option>
            <option value="seedling">SEEDLING</option>
            <option value="merchandise">MERCHANDISE</option>
            <option value="tag_stock">TAG STOCK</option>
          </select>
        </Field>
        <Field label="STOCK"><TextInput type="number" value={newItem.stock} onChange={(e) => setNewItem({ ...newItem, stock: e.target.value })} data-testid="new-inventory-stock" className="w-24" /></Field>
        <Field label="LOW ALERT AT"><TextInput type="number" value={newItem.low_stock_threshold} onChange={(e) => setNewItem({ ...newItem, low_stock_threshold: e.target.value })} className="w-24" /></Field>
        <button type="submit" data-testid="add-inventory-btn" className="inline-flex items-center gap-2 rounded-full bg-plum text-cream px-6 py-2.5 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-magenta">
          <Plus className="w-4 h-4" /> ADD ITEM
        </button>
      </form>

      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORIES.map((c) => (
          <button key={c.value} onClick={() => setFilter(c.value)} data-testid={`inventory-filter-${c.value}`} className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] font-semibold transition-colors ${filter === c.value ? "bg-plum text-cream" : "bg-warmwhite border border-plum-deep/15 text-plum-deep hover:border-magenta"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {items === null ? (
        <p className="text-plum-deep/60">Loading inventory…</p>
      ) : filtered.length === 0 ? (
        <p className="text-plum-deep/60" data-testid="inventory-empty">Nothing in this category yet.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => {
            const low = item.stock <= item.low_stock_threshold;
            return (
              <div key={item.id} className={`flex items-center gap-4 bg-warmwhite border rounded-[1.25rem] px-5 py-3.5 ${low ? "border-orange/50" : "border-plum-deep/10"}`} data-testid={`inventory-row-${item.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{item.name}</p>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-plum-deep/50 font-semibold">{item.category.replace("_", " ")}{item.variety_id ? " · LINKED TO VARIETY" : ""}</p>
                </div>
                {low && <span className="hidden sm:inline-flex items-center gap-1 text-orange text-[10px] uppercase font-bold tracking-[0.1em]" data-testid={`inventory-low-${item.id}`}><AlertTriangle className="w-3 h-3" /> LOW</span>}
                <div className="inline-flex items-center gap-3 bg-cream rounded-full px-2 py-1.5">
                  <button onClick={() => adjust(item, -1)} data-testid={`inventory-minus-${item.id}`} className="w-7 h-7 rounded-full bg-blush flex items-center justify-center hover:bg-magenta hover:text-cream transition-colors" aria-label="Decrease"><Minus className="w-3.5 h-3.5" /></button>
                  <span className={`font-serif-display text-xl w-12 text-center ${low ? "text-orange" : "text-plum-deep"}`} data-testid={`inventory-stock-${item.id}`}>{item.stock}</span>
                  <button onClick={() => adjust(item, 1)} data-testid={`inventory-plus-${item.id}`} className="w-7 h-7 rounded-full bg-blush flex items-center justify-center hover:bg-forest hover:text-cream transition-colors" aria-label="Increase"><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <button onClick={() => remove(item)} className="text-plum-deep/40 hover:text-red-600" data-testid={`inventory-delete-${item.id}`} aria-label="Delete item"><Trash2 className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Inventory;
